import { find, findIndex, remove, cloneDeep } from 'lodash';
import { DataStoreMockPlugin } from 'ui-common';
import { AgentStudioDataSource, AgentStudioDataSourceLink, IndexTarget } from '../../../components/app-studio/agent-studio/interfaces';
import { indexTargets } from './agent-studio-store-mock-data';

export class AgentStudioDataSourceStoreMockPlugin extends DataStoreMockPlugin {
  private indexTargetId: IndexTarget['id'] | null = null;
  private mockDataSources: AgentStudioDataSource[] = [];
  private initialized = false;

  mockApis: string[] = [
    'initializeStore',
    'getAgentStudioDataSourceTypes',
    'getIndexTargetDataSourceLink',
    'createIndexTargetDataSourceLink',
    'updateIndexTargetDataSourceLink',
    'removeIndexTargetDataSourceLink',
    'getDataSourceCounts',
    'getDataSourceStats',
    ...DataStoreMockPlugin.crudMockApis
  ];

  /**
   * Initialize store with a specific index target ID
   * This MUST be called before any data operations
   */
  async initializeStore(indexTargetId: IndexTarget['id']): Promise<boolean> {
    this.indexTargetId = indexTargetId || indexTargets[0].id;
    this.store.setApiConfig({
      prop: 'pathParams',
      value: { indexTargetId: this.indexTargetId }
    });
    this.initialized = true;
    if (this.mockDataSources.length === 0) {
      await this.getMockData();
    }
    return true;
  }

  /**
   * Get mock data sources for testing
   */
  async getMockData(): Promise<any> {
    try {
      const mockModule = await import('./agent-studio-data-source-store-mock-data');
      this.mockDataSources = cloneDeep(mockModule.directArrayData || []);
      this.mockDataSources.forEach(ds => {
        if (!ds.credentials) {
          ds.credentials = { type: 'GCS_SA', jsonKey: '' };
        }
        if (!ds.links) {
          ds.links = [];
        }
      });
      return {
        content: this.mockDataSources,
        totalElements: this.mockDataSources.length,
        numberOfElements: this.mockDataSources.length,
        size: this.mockDataSources.length,
        empty: this.mockDataSources.length === 0
      };
    } catch (error) {
      console.error('Error loading mock data sources:', error);
      return { content: [], totalElements: 0, numberOfElements: 0, size: 0, empty: true };
    }
  }

  /**
   * Load data sources filtered by the current index target ID
   */
  protected async load(): Promise<AgentStudioDataSource[]> {
    try {
      if (!this.initialized) {
        await this.initializeStore(null);
      }
      if (this.mockDataSources.length === 0) {
        await this.getMockData();
      }
      let filteredData = this.mockDataSources;
      if (this.indexTargetId) {
        filteredData = this.mockDataSources.filter(ds =>
          ds.links?.some(link => link.indexTargetId === this.indexTargetId)
        );
      }
      this.store.meta.data = filteredData;
      this.store.setData(filteredData, { action: 'load' });
      return filteredData;
    } catch (error) {
      console.error('Error loading data sources:', error);
      return [];
    }
  }

  /**
   * Get a specific data source by ID
   */
  protected async getItem(itemId: string): Promise<AgentStudioDataSource | null> {
    const dataSource = find(this.mockDataSources, { id: itemId });
    if (dataSource) {
      dataSource.credentials = dataSource.credentials || {
        type: 'GCS_SA',
        jsonKey: ''
      };
      dataSource.links = dataSource.links || [];
    }
    return dataSource ? dataSource : null;
  }

  /**
   * Add a new data source
   */
  protected async addItem(data: any): Promise<AgentStudioDataSource> {
    const newId = data.id || `ds-${Date.now()}`;
    const newDataSource: AgentStudioDataSource = {
      ...data,
      id: newId,
      state: 'ACTIVE',
      credentials: data.credentials || {
        type: 'GCS_SA',
        jsonKey: ''
      },
      links: []
    };
    if (this.indexTargetId && !newDataSource.links.some(link =>
      link.indexTargetId === this.indexTargetId)) {
      newDataSource.links.push({
        indexTargetId: this.indexTargetId,
        labels: []
      });
    }
    this.mockDataSources.push(newDataSource);
    this.store.setData(this.mockDataSources, { action: 'add', item: newDataSource });
    return newDataSource;
  }

  /**
   * Update an existing data source
   */
  protected async updateItem(item: any): Promise<AgentStudioDataSource | null> {
    const index = findIndex(this.mockDataSources, { id: item.id });
    if (index !== -1) {
      const updatedDataSource = {
        ...this.mockDataSources[index],
        ...item,
        links: item.links || this.mockDataSources[index].links || []
      };
      this.mockDataSources[index] = updatedDataSource;
      this.store.setData(this.mockDataSources, { action: 'update', item: updatedDataSource });
      return updatedDataSource;
    }
    return null;
  }

  /**
   * Remove a data source
   */
  protected async removeItem(id: any): Promise<AgentStudioDataSource[]> {
    const dataSource = find(this.mockDataSources, { id });
    if (dataSource) {
      remove(this.mockDataSources, { id });
      this.store.setData(this.mockDataSources, { action: 'remove', item: dataSource });
      return [dataSource];
    }
    return [];
  }

  /**
   * Get link between index target and data source
   */
  async getIndexTargetDataSourceLink(indexTargetId: string, dataSourceId: string): Promise<AgentStudioDataSourceLink | null> {
    const dataSource = find(this.mockDataSources, { id: dataSourceId });
    if (dataSource && dataSource.links) {
      return find(dataSource.links, { indexTargetId }) || null;
    }
    return null;
  }

  /**
   * Create link between index target and data source
   */
  async createIndexTargetDataSourceLink(dataSourceLink: AgentStudioDataSourceLink, dataSourceId: string): Promise<AgentStudioDataSourceLink | null> {
    const dataSource = find(this.mockDataSources, { id: dataSourceId });
    if (dataSource) {
      if (!dataSource.links) {
        dataSource.links = [];
      }
      const link: AgentStudioDataSourceLink = {
        indexTargetId: dataSourceLink.indexTargetId,
        labels: dataSourceLink.labels || []
      };
      dataSource.links.push(link);
      this.store.setData(this.mockDataSources, { action: 'update', item: dataSource });
      return link;
    }
    return null;
  }

  /**
   * Update link between index target and data source
   */
  async updateIndexTargetDataSourceLink(dataSourceLink: AgentStudioDataSourceLink, dataSourceId: string): Promise<AgentStudioDataSourceLink | null> {
    const dataSource = find(this.mockDataSources, { id: dataSourceId });
    if (dataSource && dataSource.links) {
      const linkIndex = findIndex(dataSource.links, { indexTargetId: dataSourceLink.indexTargetId });
      if (linkIndex !== -1) {
        dataSource.links[linkIndex] = {
          ...dataSource.links[linkIndex],
          ...dataSourceLink
        };
        this.store.setData(this.mockDataSources, { action: 'update', item: dataSource });
        return dataSource.links[linkIndex];
      }
    }
    return null;
  }

  /**
   * Remove link between index target and data source
   */
  async removeIndexTargetDataSourceLink(indexTargetId: string, dataSourceId: string): Promise<AgentStudioDataSourceLink | null> {
    const dataSource = find(this.mockDataSources, { id: dataSourceId });
    if (dataSource && dataSource.links) {
      const linkIndex = findIndex(dataSource.links, { indexTargetId });
      if (linkIndex !== -1) {
        const removedLink = dataSource.links[linkIndex];
        dataSource.links.splice(linkIndex, 1);
        this.store.setData(this.mockDataSources, { action: 'update', item: dataSource });
        return removedLink;
      }
    }
    return null;
  }

  /**
   * Get data source types - simplified for testing
   */
  getAgentStudioDataSourceTypes(): any[] {
    return [
      {
        type: 'GCS_PRODUCT_CATALOG',
        name: 'Google Cloud Storage Product Catalog',
        description: 'Import product data from a Google Cloud Storage bucket'
      }
    ];
  }

  /**
   * Get data source count
   */
  getDataSourceCounts(): number {
    return this.mockDataSources.length;
  }

  async getDataSourceStats(_customerId: string, _dataSourceId: string): Promise<any> {
    const fixedDocsCount = 100;

    return Promise.resolve({
      totalDocs: fixedDocsCount,
      groups: [
        {
          groupingField: "datasource_id",
          values: [
            {
              id: "mock_ds_value_id_1",
              docsCount: fixedDocsCount > 0 ? Math.floor(fixedDocsCount * 0.6) : 0
            },
            {
              id: "mock_ds_value_id_2",
              docsCount: fixedDocsCount > 0 ? Math.ceil(fixedDocsCount * 0.4) : 0
            }
          ]
        }
      ]
    });
  }

}